import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { NavLink } from "react-router-dom"
import { api } from "@/api/client"
import { MODULE } from "@/App"
import { SkeletonKarten, SkeletonZeilen } from "@/lib/Skeleton"
import { formatDatum, formatKwh } from "@/lib/format"
import { useDocumentTitle } from "@/lib/useDocumentTitle"
import type { DashboardResponse, ZeitraumModus } from "@/api/types"

interface Health {
  status: string
  app: string
  version: string
}

function heute() {
  return new Date()
}

function isoWoche(d: Date): number {
  const target = new Date(d.valueOf())
  const dayNr = (d.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = new Date(target.getFullYear(), 0, 4)
  const diff = target.valueOf() - firstThursday.valueOf()
  return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000))
}

// Zeitraum-Umschalter: 3 Modi (ARCHITEKTUR.md E5) — reiner Frontend-State,
// KEIN DB-Singleton (ersetzt Ninox "Diagramme #1", 03_logik.md Regel 15-20).
function useZeitraumState() {
  const [modus, setModus] = useState<ZeitraumModus>("monat")
  const [jahr, setJahr] = useState(heute().getFullYear())
  const [monat, setMonat] = useState(heute().getMonth() + 1)
  const [kw, setKw] = useState(isoWoche(heute()))
  const [plusWochen, setPlusWochen] = useState(0)
  const [von, setVon] = useState(() => heute().toISOString().slice(0, 10))
  const [bis, setBis] = useState(() => heute().toISOString().slice(0, 10))

  return {
    modus,
    setModus,
    jahr,
    setJahr,
    monat,
    setMonat,
    kw,
    setKw,
    plusWochen,
    setPlusWochen,
    von,
    setVon,
    bis,
    setBis,
  }
}

export function DashboardPage() {
  useDocumentTitle("Dashboard")
  const z = useZeitraumState()

  const health = useQuery({
    queryKey: ["health"],
    queryFn: async () => (await api.get<Health>("/health")).data,
  })

  const dashboardParams =
    z.modus === "monat"
      ? { modus: "monat", jahr: z.jahr, monat: z.monat }
      : z.modus === "kalenderwoche"
        ? { modus: "kalenderwoche", jahr: z.jahr, kw: z.kw, plusWochen: z.plusWochen }
        : { modus: "frei", von: z.von, bis: z.bis }

  const dashboardQuery = useQuery({
    queryKey: ["dashboard", dashboardParams],
    queryFn: async () =>
      (await api.get<DashboardResponse>("/dashboard", { params: dashboardParams })).data,
  })

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">Energiemonitoring-Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">
        Zeitraum-Filter (Monat / ISO-Kalenderwoche+N / freier Bereich), bis zu 5 Zähler,
        Summenbildung serverseitig (F1, AP-6.6).
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex gap-1 rounded-md bg-slate-100 p-1">
          {(["monat", "kalenderwoche", "frei"] as const).map((m) => (
            <button
              key={m}
              onClick={() => z.setModus(m)}
              className={`rounded-md px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                z.modus === m ? "bg-white shadow-sm" : "text-slate-500"
              }`}
            >
              {m === "monat" ? "Monat" : m === "kalenderwoche" ? "Kalenderwoche" : "Freier Bereich"}
            </button>
          ))}
        </div>

        {z.modus === "monat" && (
          <>
            <div>
              <label className="block text-xs text-slate-500">Jahr</label>
              <input
                type="number"
                value={z.jahr}
                onChange={(e) => z.setJahr(Number(e.target.value))}
                className="h-9 w-24 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500">Monat</label>
              <input
                type="number"
                min={1}
                max={12}
                value={z.monat}
                onChange={(e) => z.setMonat(Number(e.target.value))}
                className="h-9 w-20 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
          </>
        )}

        {z.modus === "kalenderwoche" && (
          <>
            <div>
              <label className="block text-xs text-slate-500">Jahr</label>
              <input
                type="number"
                value={z.jahr}
                onChange={(e) => z.setJahr(Number(e.target.value))}
                className="h-9 w-24 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500">KW</label>
              <input
                type="number"
                min={1}
                max={53}
                value={z.kw}
                onChange={(e) => z.setKw(Number(e.target.value))}
                className="h-9 w-20 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500">+ Wochen</label>
              <input
                type="number"
                min={0}
                value={z.plusWochen}
                onChange={(e) => z.setPlusWochen(Number(e.target.value))}
                className="h-9 w-20 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
          </>
        )}

        {z.modus === "frei" && (
          <>
            <div>
              <label className="block text-xs text-slate-500">Von</label>
              <input
                type="date"
                value={z.von}
                onChange={(e) => z.setVon(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500">Bis</label>
              <input
                type="date"
                value={z.bis}
                onChange={(e) => z.setBis(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
              />
            </div>
          </>
        )}

        {dashboardQuery.data && (
          <span className="ml-auto text-xs text-slate-400">
            {formatDatum(dashboardQuery.data.zeitraum.von)} – {formatDatum(dashboardQuery.data.zeitraum.bis)}
          </span>
        )}
      </div>

      {dashboardQuery.isLoading ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SkeletonKarten anzahl={3} />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Backend</div>
            <div className="mt-1 text-lg font-medium">
              {health.isLoading && "prüft…"}
              {health.isError && <span className="text-red-600">nicht erreichbar</span>}
              {health.data && (
                <span className="text-green-600">
                  {health.data.app} v{health.data.version} — {health.data.status}
                </span>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Zähler im Zeitraum</div>
            <div className="mt-1 text-lg font-medium">
              {dashboardQuery.data ? dashboardQuery.data.zaehler.length : "—"}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Messsystem-Anbindung</div>
            <div className="mt-1 text-lg font-medium text-slate-600">
              manuell + Push-/CSV-Adapter
            </div>
          </div>
        </div>
      )}

      {dashboardQuery.isError && (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Dashboard-Daten konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => dashboardQuery.refetch()}
            className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2 font-semibold">Zähler</th>
              <th className="px-4 py-2 font-semibold">Tage mit Werten</th>
              <th className="px-4 py-2 font-semibold">Summe im Zeitraum</th>
            </tr>
          </thead>
          <tbody>
            {dashboardQuery.isLoading && <SkeletonZeilen zeilen={4} spalten={3} />}
            {dashboardQuery.data?.zaehler.map((z2) => (
              <tr
                key={z2.zaehlerId}
                className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                onClick={() => (window.location.href = `/zaehler/${z2.zaehlerId}`)}
              >
                <td className="px-4 py-2.5 font-medium text-slate-900">{z2.displayName}</td>
                <td className="px-4 py-2.5 text-slate-700">{z2.punkte.length}</td>
                <td className="px-4 py-2.5 text-slate-700">{formatKwh(z2.summe)}</td>
              </tr>
            ))}
            {dashboardQuery.data?.zaehler.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                  Keine relevanten Zähler mit Werten im Zeitraum — Filter oben anpassen oder
                  Zählerstände unter „Zähler" erfassen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-lg font-semibold text-slate-900">Module</h2>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {MODULE.map((m) => (
          <NavLink
            key={m.pfad}
            to={`/${m.pfad}`}
            className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-400"
          >
            <div className="font-medium text-slate-900">{m.titel}</div>
            <div className="mt-1 text-sm text-slate-500">{m.untertitel}</div>
          </NavLink>
        ))}
      </div>
    </div>
  )
}
